import { useTranslation } from 'react-i18next';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';

/**
 * Single i18n indirection for the display noun of the `teams` entity.
 *
 * The account now displays **"Teamspace"** everywhere the UI used to show "Team" OR "Workspace"
 * (Ted, 2026-09-02). Both former branches collapse to one word, so this hook no longer varies the
 * noun by account — it stays the single seam so the wording can be changed in one place again.
 *
 * This is a **display-only** relabel: the `teams` entity, `teamId`, `workspaceIsolation` and every
 * backend path are unchanged. Only the rendered word moved.
 *
 * Usage — prefer this over branching or hardcoding at each string site:
 *  - Bare noun:   `const { noun, nounPlural } = useTeamNoun();` → render `{noun}`
 *  - Phrase:      `t('Add {{noun}}', { noun })` / `t('No {{noun}} found', { noun: nounPluralLower })`
 *
 * NOT in scope for the relabel — these "Workspace"/"Teams" are other companies' product names and
 * must keep their own spelling: Google Workspace (`features/connect/pages/GoogleMeetPage`),
 * Databricks Workspace (`features/integrations/.../DatabricksConnectionDialog`), OptiDev workspaces
 * (`features/optidev`), and Microsoft Teams (`features/connect/*Teams*`).
 *
 * The keys `Teamspace`/`Teamspaces`/`teamspace`/`teamspaces` live in `public/i18n/en.json`.
 */
export function useTeamNoun() {
  const { t } = useTranslation();
  const { workspaceIsolation } = useCompanyFeatures();

  return {
    /**
     * Whether the account has the `workspaceIsolation` feature on.
     *
     * NOTE: this no longer selects the noun — it is the same "Teamspace" either way. It remains
     * exposed only for callers that need the FEATURE (isolation behaviour), not the label. Do not
     * reintroduce `isWorkspace ? 'Workspace …' : 'Team …'` label picks; say "Teamspace …".
     */
    isWorkspace: workspaceIsolation,
    /** Title-case singular. */
    noun: t('Teamspace'),
    /** Title-case plural. */
    nounPlural: t('Teamspaces'),
    /** Lowercase singular for mid-sentence use. */
    nounLower: t('teamspace'),
    /** Lowercase plural for mid-sentence use. */
    nounPluralLower: t('teamspaces'),
  };
}
